import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { api } from "../../lib/api";

export interface Facets {
  collections: string[];
  projects: string[];
  kinds: string[];
  langs: string[];
  status: string[];
  tags: string[];
}

export const EMPTY_FACETS: Facets = {
  collections: [],
  projects: [],
  kinds: [],
  langs: [],
  status: [],
  tags: [],
};

interface FacetRailProps {
  facets: Facets;
  onChange: (f: Facets) => void;
  dynamicCounts?: Record<string, Record<string, number>>;
}

/**
 * FacetRail — facets are sourced from /v2/search/facets so counts reflect
 * the real corpus, not hardcoded lists. `dynamicCounts` (per-search result
 * histograms) layer on top to show the user how many of each remain in the
 * current search.
 */
export function FacetRail({ facets, onChange, dynamicCounts }: FacetRailProps) {
  const facetsQuery = useQuery({
    queryKey: ["search-facets"],
    queryFn: () => api.getSearchFacets(),
    staleTime: 60_000,
  });
  const [tagQuery, setTagQuery] = useState("");

  function toggle<K extends keyof Facets>(group: K, value: string): void {
    const cur = facets[group];
    const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
    onChange({ ...facets, [group]: next });
  }

  function count(group: string, value: string): number | undefined {
    return dynamicCounts?.[group]?.[value];
  }

  const data = facetsQuery.data;

  const visibleTags = useMemo(() => {
    if (!data) return [];
    const q = tagQuery.trim().toLowerCase();
    if (!q) return data.tags.slice(0, 20);
    return data.tags.filter((t) => t.tag.toLowerCase().includes(q)).slice(0, 30);
  }, [data, tagQuery]);

  if (!data) {
    return <p className="text-xs text-(--text-tertiary)">Cargando facetas…</p>;
  }

  const hasAny =
    facets.collections.length +
      facets.projects.length +
      facets.kinds.length +
      facets.langs.length +
      facets.status.length +
      facets.tags.length >
    0;

  return (
    <nav aria-label="Filtros" className="space-y-4 text-sm" data-testid="facet-rail">
      {data.collections.length > 0 && (
        <Group title="Colección">
          {data.collections.slice(0, 12).map((c) => (
            <Checkbox
              key={c.value}
              label={c.label}
              count={count("collection", c.value) ?? c.count}
              checked={facets.collections.includes(c.value)}
              onToggle={() => toggle("collections", c.value)}
            />
          ))}
        </Group>
      )}

      {data.kinds.length > 0 && (
        <Group title="Tipo">
          {data.kinds.slice(0, 10).map((k) => (
            <Checkbox
              key={k.value}
              label={k.value}
              count={count("kind", k.value) ?? k.count}
              checked={facets.kinds.includes(k.value)}
              onToggle={() => toggle("kinds", k.value)}
            />
          ))}
        </Group>
      )}

      {data.langs.length > 0 && (
        <Group title="Idioma">
          {data.langs.map((l) => (
            <Checkbox
              key={l.value}
              label={l.value}
              count={count("lang", l.value) ?? l.count}
              checked={facets.langs.includes(l.value)}
              onToggle={() => toggle("langs", l.value)}
            />
          ))}
        </Group>
      )}

      {data.status.length > 0 && (
        <Group title="Estado">
          {data.status.map((s) => (
            <Checkbox
              key={s.value}
              label={s.value}
              count={count("status", s.value) ?? s.count}
              checked={facets.status.includes(s.value)}
              onToggle={() => toggle("status", s.value)}
            />
          ))}
        </Group>
      )}

      {data.tags.length > 0 && (
        <Group title="Etiquetas">
          <input
            value={tagQuery}
            onChange={(e) => setTagQuery(e.target.value)}
            placeholder="Buscar etiqueta…"
            className="mb-1 h-7 w-full rounded-md border border-(--border-subtle) bg-(--bg-base) px-2 text-xs focus:outline-none focus:ring-1 focus:ring-(--accent-500)"
          />
          {visibleTags.length === 0 && (
            <p className="px-1 py-1 text-[11px] text-(--text-tertiary)">Sin coincidencias</p>
          )}
          {visibleTags.map((t) => (
            <Checkbox
              key={t.tag}
              label={`#${t.tag}`}
              count={count("tag", t.tag) ?? t.count}
              checked={facets.tags.includes(t.tag)}
              onToggle={() => toggle("tags", t.tag)}
            />
          ))}
        </Group>
      )}

      {hasAny && (
        <button
          type="button"
          className="w-full rounded-md border border-(--border-subtle) px-3 py-1.5 text-xs text-(--text-secondary) hover:bg-(--bg-hover)"
          onClick={() => onChange(EMPTY_FACETS)}
        >
          Limpiar filtros
        </button>
      )}
    </nav>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
        {title}
      </h3>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function Checkbox({
  label,
  count,
  checked,
  onToggle,
}: {
  label: string;
  count?: number;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-(--bg-hover)">
      <input type="checkbox" checked={checked} onChange={onToggle} className="rounded" />
      <span className="flex-1 truncate">{label}</span>
      {count != null && <span className="text-[10px] text-(--text-tertiary)">{count}</span>}
    </label>
  );
}
